import React, { useState, useEffect, useRef } from "react";
import logo from "../assets/images/logo.png";
import { NavLink } from "react-router-dom";
import { useCloud } from "../hooks/useCloud";
import { useNavigate } from "react-router-dom";
import {
  CircleX,
  LogOut,
  LayoutGrid,
  BarChart3,
  Utensils,
  ChartPie,
  Package,
  Settings,
  Logs,
  Lock,
  Wallet,
} from "lucide-react";
import store from "../store";
import usePlan from "../hooks/usePlan";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { isExpired, isDue } = usePlan();
  const navigate = useNavigate();
  const { syncCloud } = useCloud();
  const [isExpanded, setIsExpanded] = useState(false);
  const mobileSidebarRef = useRef(null);

  // Determine if features should be locked based on plan status
  // Only lock for expired plans, allow navigation for due payments
  const shouldLockFeatures = isExpired;

  const handleLogOut = async () => {
    await syncCloud;
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("store");
    localStorage.removeItem("processedBills");
    localStorage.removeItem("partnerOrders");
    localStorage.removeItem("name");
    localStorage.removeItem("lastBillNo");
    localStorage.removeItem("isGST");
    localStorage.removeItem("email");
    localStorage.removeItem("canclepassword");
    localStorage.removeItem("address");
    store.resetStore();
    navigate("/login");
  };

  const renderSidebarContent = (isMobile = false) => {
    // For mobile, always use expanded state
    const shouldExpand = isMobile ? true : isExpanded;

    // Helper function to render navigation items with lock functionality
    const renderNavItem = (to, icon, text, isLocked = false) => {
      const handleClick = (e) => {
        if (isLocked) {
          e.preventDefault();
          return;
        }
        toggleSidebar();
      };

      return (
        <NavLink
          to={to}
          onClick={handleClick}
          className={({ isActive }) =>
            `sidebar-nav-item ${
              !shouldExpand ? "sidebar-nav-item--collapsed" : ""
            } ${isActive ? "active" : ""} ${isLocked ? "locked" : ""}`
          }
          style={isLocked ? { pointerEvents: "none", opacity: 0.5 } : {}}
        >
          {icon}
          <span
            className={`sidebar-nav-text ${
              shouldExpand
                ? "sidebar-nav-text--expanded"
                : "sidebar-nav-text--collapsed"
            }`}
          >
            {text}
          </span>
          {isLocked && (
            <Lock
              size={16}
              className="sidebar-nav-lock-icon "
              style={{
                marginLeft: "auto",
                opacity: 0.7,
              }}
            />
          )}
        </NavLink>
      );
    };

    return (
      <div className="sidebar-content">
        {/* Logo Section - Centered */}
        <NavLink
          to="/"
          className={`sidebar-logo ${
            shouldExpand
              ? "sidebar-logo--expanded"
              : "sidebar-logo--collapsed px-0"
          }`}
          onClick={toggleSidebar}
        >
          <img src={logo} alt="Logo" className="sidebar-logo-image" />
          <h1
            className={`sidebar-logo-text ${
              shouldExpand
                ? "sidebar-logo-text--expanded"
                : "sidebar-logo-text--collapsed"
            }`}
          >
            QRAPID
          </h1>
        </NavLink>

        {/* Navigation Items */}
        <nav className="sidebar-nav mt-10">
          {renderNavItem(
            "/table-overview",
            <LayoutGrid strokeWidth={1.5} className="sidebar-nav-icon" />,
            "Table"
          )}

          {renderNavItem(
            "/dashboard",
            <BarChart3 strokeWidth={1.5} className="sidebar-nav-icon" />,
            "Dashboard"
          )}

          {renderNavItem(
            "/menu-managment",
            <Utensils strokeWidth={1.5} className="sidebar-nav-icon" />,
            "Menu"
          )}

          {renderNavItem(
            "/order-main",
            <Logs strokeWidth={1.5} className="sidebar-nav-icon" />,
            "Orders",
            shouldLockFeatures // Lock Orders for expired/due plans
          )}

          {renderNavItem(
            "/reports",
            <ChartPie strokeWidth={1.5} className="sidebar-nav-icon" />,
            "Reports",
            shouldLockFeatures // Lock Reports for expired/due plans
          )}

          {renderNavItem(
            "/inventory",
            <Package strokeWidth={1.5} className="sidebar-nav-icon" />,
            "Inventory",
            shouldLockFeatures // Lock Inventory for expired/due plans
          )}

          {renderNavItem(
            "/expenses",
            <Wallet strokeWidth={1.5} className="sidebar-nav-icon" />,
            "Expenses"
          )}

          {renderNavItem(
            "/setting",
            <Settings strokeWidth={1.5} className="sidebar-nav-icon" />,
            "Settings"
          )}
        </nav>

        {/* Logout Button - At the bottom */}
        <div className="sidebar-logout-container">
          <button
            onClick={handleLogOut}
            className={`sidebar-logout-button ${
              shouldExpand
                ? "sidebar-logout-button--expanded"
                : "sidebar-logout-button--collapsed"
            }`}
          >
            <LogOut className="sidebar-logout-icon" />
            <span
              className={`sidebar-logout-text ${
                shouldExpand
                  ? "sidebar-logout-text--expanded"
                  : "sidebar-logout-text--collapsed"
              }`}
            >
              Logout
            </span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile Backdrop/Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Mobile Sidebar (Drawer) */}
      <aside
        ref={mobileSidebarRef}
        id="mobile-sidebar"
        className={`sidebar-mobile ${
          isOpen ? "sidebar-mobile--open" : "sidebar-mobile--closed"
        }`}
        style={{
          zIndex: 50,
        }}
      >
        <div className="relative">
          <button className="sidebar-mobile-close" onClick={toggleSidebar}>
            <CircleX className="sidebar-mobile-close-icon" />
          </button>
        </div>
        {renderSidebarContent(true)}
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className="sidebar-container"
        style={{
          zIndex: 10,
          width: isExpanded ? "240px" : "80px",
        }}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {renderSidebarContent(false)}
      </aside>
    </>
  );
};

export default Sidebar;
